import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  LocalFireDepartment as FireIcon,
  TrendingUp as TrendingUpIcon,
  Article as ArticleIcon,
} from "@mui/icons-material";
import { useTheme } from "./ThemeContext";

const API_BASE_URL = process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";

function LeftSidebarPosts() {
  const { theme } = useTheme();
  const { postId } = useParams();
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [hotPosts, setHotPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPostTags, setCurrentPostTags] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (postId) {
          const postRes = await fetch(`${API_BASE_URL}/api/posts/byId?postId=${postId}`);
          if (postRes.ok) {
            const postData = await postRes.json();
            setCurrentPostTags(postData.tags || []);

            if (postData.tags && postData.tags.length > 0) {
              const firstTag = postData.tags[0];
              const relatedRes = await fetch(
                `${API_BASE_URL}/api/trending/ticker/${encodeURIComponent(firstTag)}/posts/hot?page=0&size=5`
              );
              if (relatedRes.ok) {
                const relatedData = await relatedRes.json();
                setRelatedPosts(relatedData.filter(p => p.postId !== parseInt(postId)));
              }
            }
          }
        }

        const hotRes = await fetch(`${API_BASE_URL}/api/trending/posts/hot?page=0&size=5`);
        if (hotRes.ok) {
          const hotData = await hotRes.json();
          setHotPosts(hotData.filter(p => p.postId !== parseInt(postId)));
        }
      } catch (err) {
        console.error("Error fetching related posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [postId]);

  const getRankStyle = (index) => {
    if (index === 0) return { color: "#FFD700", fontWeight: 700 };
    if (index === 1) return { color: "#C0C0C0", fontWeight: 600 };
    if (index === 2) return { color: "#CD7F32", fontWeight: 600 };
    return { color: theme.palette.text.disabled, fontWeight: 500 };
  };

  const SectionHeader = ({ icon: Icon, title, gradient }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        pb: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          background: gradient || `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
        }}
      >
        <Icon sx={{ fontSize: 18, color: "#fff" }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          fontSize: "0.9rem",
          letterSpacing: "0.3px",
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const PostItem = ({ post, index }) => (
    <Link to={`/post/${post.postId}`} style={{ textDecoration: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          py: 1.5,
          px: 1.5,
          borderRadius: 2,
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: "translateX(4px)",
          },
        }}
      >
        <Typography
          sx={{
            ...getRankStyle(index),
            fontSize: "0.9rem",
            minWidth: 24,
            textAlign: "center",
          }}
        >
          {index + 1}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontSize: "0.85rem",
              fontWeight: 500,
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              mb: 0.5,
            }}
          >
            {post.title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {post.community?.name && (
              <Typography
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.7),
                  fontSize: "0.7rem",
                }}
              >
                c/{post.community.name}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                }}
              >
                {(post.upvotes || 0) - (post.downvotes || 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Link>
  );

  const LoadingSkeleton = ({ count = 3 }) => (
    <>
      {[...Array(count)].map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 1.5 }}>
          <Skeleton variant="text" width={24} height={24} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="90%" height={20} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        </Box>
      ))}
    </>
  );

  const Section = ({ children }) => (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 3,
        p: 2,
        mb: 2,
        border: `1px solid ${alpha(theme.palette.divider || "#fff", 0.08)}`,
        boxShadow: `0 4px 20px ${alpha("#000", 0.1)}`,
        backdropFilter: "blur(10px)",
      }}
    >
      {children}
    </Box>
  );

  const EmptyState = ({ message }) => (
    <Box sx={{ py: 3, textAlign: "center" }}>
      <Typography
        sx={{
          color: alpha(theme.palette.text.secondary, 0.5),
          fontSize: "0.85rem",
          fontStyle: "italic",
        }}
      >
        {message}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 280,
        pt: 2,
        px: 2,
        pb: 4,
      }}
    >
      {/* Related Posts Section */}
      {currentPostTags.length > 0 && relatedPosts.length > 0 && (
        <Section>
          <SectionHeader
            icon={ArticleIcon}
            title={`More on $${currentPostTags[0]}`}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          />
          {relatedPosts.slice(0, 5).map((post, index) => (
            <PostItem key={post.postId} post={post} index={index} />
          ))}
        </Section>
      )}

      {/* Hot Posts Section */}
      <Section>
        <SectionHeader
          icon={FireIcon}
          title="Hot Right Now"
          gradient="linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)"
        />
        {loading ? (
          <LoadingSkeleton count={4} />
        ) : hotPosts.length > 0 ? (
          hotPosts.map((post, index) => (
            <PostItem key={post.postId} post={post} index={index} />
          ))
        ) : (
          <EmptyState message="No hot posts yet" />
        )}
      </Section>
    </Box>
  );
}

export default LeftSidebarPosts;
